import type { AssistantGeocodingContext } from '@/services/api/domains/chatbot.api'

import { getAssistantSurface } from './assistant-actions'
import type { ExecuteClientToolDeps } from './client-tool-types'

/** The pages `show_geocoding` opens, as the model names them. */
const VIEWS = ['sessions', 'wizard', 'results'] as const
type GeocodingView = (typeof VIEWS)[number]

/** The view each page reports in its assistant context (`useAssistantGeocodingSurface`). */
const SURFACE_VIEW: Record<GeocodingView, AssistantGeocodingContext['view']> = {
  sessions: 'list',
  wizard: 'wizard',
  results: 'results',
}

/** Matches the server's cap on a session id in the page context. */
const MAX_ID = 64

/** A page reads its session(s) before it reports them; past this the page is shown without a claim. */
const SHOW_WAIT_MS = 4_000
const POLL_MS = 100

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

interface ShowRequest {
  view: GeocodingView
  sessionId?: string
}

function readRequest(input: unknown): ShowRequest | { error: string } {
  const raw = (input ?? {}) as { view?: unknown; session_id?: unknown }
  const view = raw.view === undefined ? 'sessions' : raw.view

  if (!VIEWS.includes(view as GeocodingView)) {
    return { error: "Invalid view: expected 'sessions', 'wizard' or 'results'." }
  }

  const id = typeof raw.session_id === 'string' ? raw.session_id.trim() : ''

  if (raw.session_id !== undefined && (typeof raw.session_id !== 'string' || id.length > MAX_ID)) {
    return { error: 'Invalid session_id.' }
  }

  if (view === 'results' && !id) return { error: "The 'results' view needs a session_id." }

  return { view: view as GeocodingView, ...(id && view !== 'sessions' ? { sessionId: id } : {}) }
}

function pathOf(req: ShowRequest): string {
  if (req.view === 'sessions') return '/geocoding/sessions'
  if (req.view === 'results') return `/geocoding/sessions/${encodeURIComponent(req.sessionId!)}`

  return req.sessionId ? `/geocoding?resume=${encodeURIComponent(req.sessionId)}` : '/geocoding'
}

/** True once the geocoding page on screen reports the requested view (and session), loaded. */
function showing(req: ShowRequest): boolean {
  const handle = getAssistantSurface()
  if (handle?.kind !== 'geocoding') return false

  const ctx = handle.readContext().geocoding
  if (ctx.view !== SURFACE_VIEW[req.view] || ctx.loading) return false

  return !req.sessionId || ctx.session?.id === req.sessionId
}

async function awaitShowing(req: ShowRequest): Promise<boolean> {
  const deadline = Date.now() + SHOW_WAIT_MS

  for (;;) {
    if (showing(req)) return true
    if (Date.now() >= deadline) return false
    await delay(POLL_MS)
  }
}

/**
 * `show_geocoding`. `refusal` is the route manifest's answer for `/geocoding` for this caller,
 * resolved by `executeClientTool` (which owns the resolver), or null when allowed.
 */
export async function executeShowGeocoding(
  input: unknown,
  deps: ExecuteClientToolDeps,
  refusal: string | null,
): Promise<unknown> {
  const req = readRequest(input)
  if ('error' in req) return req

  if (refusal) return { refused: refusal }

  const target = pathOf(req)
  const [targetPath, targetQuery = ''] = target.split('?')
  const onTargetPage = deps.currentRoute() === targetPath
  const discard = (input as { discardUnsaved?: unknown } | null)?.discardUnsaved === true

  if (!onTargetPage && !discard) {
    const unsaved = deps.hasUnsavedWork()
    if (unsaved) return { blocked_unsaved_work: unsaved }
  }

  // Showing what is already shown must not add a Back entry.
  const currentQuery = (deps.currentSearch?.() ?? '').replace(/^\?/, '')
  if (onTargetPage && currentQuery === targetQuery) deps.navigate(target, { replace: true })
  else deps.navigate(target)

  return (await awaitShowing(req))
    ? { shown: true, view: req.view }
    : { shown: true, note: 'The geocoding page did not report this view in time; it is open but may still be loading.' }
}
